import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Search, Loader2, Eye } from "lucide-react";

const STATUS_VARIANT = {
  active: "bg-primary/15 text-primary border-primary/30",
  trial: "bg-warning/15 text-warning border-warning/30",
  expired: "bg-destructive/15 text-destructive border-destructive/30",
  suspended: "bg-muted text-muted-foreground border-border",
};

const EMPTY = {
  company_name: "", center_name: "", owner_name: "", mobile: "", email: "", gst: "",
  address: "", state: "", district: "", village: "", subdomain: "", plan_key: "starter", status: "trial",
};

export default function Tenants() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const { data: plans } = useQuery({ queryKey: ["plans"], queryFn: async () => (await api.get("/platform/plans")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["tenants", page, search, statusFilter],
    queryFn: async () =>
      (await api.get("/platform/tenants", { params: { page, page_size: 10, search, status: statusFilter === "all" ? "" : statusFilter } })).data,
  });

  const create = useMutation({
    mutationFn: async (body) => (await api.post("/platform/tenants", body)).data,
    onSuccess: (res) => {
      toast.success(`Tenant created. Owner temp password: ${res.owner_temp_password}`, { duration: 10000 });
      setOpen(false);
      setForm(EMPTY);
      qc.invalidateQueries({ queryKey: ["tenants"] });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const statusLabel = (s) => ({ active: t("common.active"), trial: t("pd.trial"), expired: t("pd.expired"), suspended: t("td.suspend") }[s] || s);

  return (
    <div className="space-y-6" data-testid="tenants-page">
      <PageHeader
        title={t("tenants.title")}
        subtitle={t("tenants.subtitle")}
        breadcrumb={[t("crumb.platform"), t("tenants.title")]}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-tenant-btn"><Plus className="mr-1.5 h-4 w-4" /> {t("tenants.new")}</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
              <DialogHeader><DialogTitle className="font-heading">Create Milk Collection Center</DialogTitle></DialogHeader>
              <div className="grid gap-4 py-2 sm:grid-cols-2">
                {[
                  ["company_name", "Company Name *"], ["center_name", "Collection Center Name *"],
                  ["owner_name", "Owner Name *"], ["mobile", "Mobile *"], ["email", "Owner Email *"],
                  ["subdomain", "Subdomain *"], ["gst", "GST"], ["state", "State"],
                  ["district", "District"], ["village", "Village"], ["address", "Address"],
                ].map(([k, label]) => (
                  <div key={k} className="space-y-1.5">
                    <Label>{label}</Label>
                    <Input value={form[k]} onChange={set(k)} data-testid={`tenant-${k}-input`}
                      placeholder={k === "subdomain" ? "abcmilk" : ""} />
                  </div>
                ))}
                <div className="space-y-1.5">
                  <Label>Subscription Plan</Label>
                  <Select value={form.plan_key} onValueChange={set("plan_key")}>
                    <SelectTrigger data-testid="tenant-plan-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(plans || []).map((p) => <SelectItem key={p.key} value={p.key}>{p.name} — ₹{p.price}/mo</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={set("status")}>
                    <SelectTrigger data-testid="tenant-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="trial">Trial (14 days)</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
                <Button onClick={() => create.mutate(form)} disabled={create.isPending} data-testid="submit-tenant-btn">
                  {create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("tenants.createBtn")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <Card>
        <div className="flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder={t("tenants.searchPlaceholder")}
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} data-testid="tenant-search-input" />
          </div>
          <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
            <SelectTrigger className="sm:w-44" data-testid="tenant-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("common.allStatuses")}</SelectItem>
              <SelectItem value="active">{t("pd.active")}</SelectItem>
              <SelectItem value="trial">{t("pd.trial")}</SelectItem>
              <SelectItem value="suspended">{t("td.suspend")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("tenants.center")}</TableHead>
                <TableHead>{t("tenants.owner")}</TableHead>
                <TableHead>{t("tenants.plan")}</TableHead>
                <TableHead className="text-right">{t("tenants.farmers")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead className="text-right">{t("common.action")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading
                ? Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}><TableCell colSpan={6}><Skeleton className="h-8" /></TableCell></TableRow>
                  ))
                : (data?.items || []).map((row) => (
                    <TableRow key={row.id} className="hover:bg-muted/50" data-testid={`tenant-row-${row.subdomain}`}>
                      <TableCell>
                        <p className="font-medium">{row.center_name}</p>
                        <p className="text-xs text-muted-foreground">{row.subdomain}.milkerp.com</p>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{row.owner_name}</p>
                        <p className="text-xs text-muted-foreground">{row.email}</p>
                      </TableCell>
                      <TableCell><Badge variant="outline">{row.plan_name}</Badge></TableCell>
                      <TableCell className="text-right tnum">{row.farmer_count}</TableCell>
                      <TableCell>
                        <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_VARIANT[row.effective_status]}`}>
                          {statusLabel(row.effective_status)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/platform/tenants/${row.id}`)} data-testid={`view-tenant-${row.subdomain}`}>
                          <Eye className="mr-1.5 h-4 w-4" /> {t("common.manage")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              {!isLoading && data?.items?.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">{t("tenants.none")}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <TablePagination page={page} pages={data?.pages} total={data?.total} onPage={setPage} />
      </Card>
    </div>
  );
}
